import { useState, useEffect, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import PublicReports from "./pages/PublicReports";
import PublicDemo from "./pages/PublicDemo";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./layouts/AppLayout";
import SplashScreen from "./components/SplashScreen";
import MapsLifecyclePanel from "./components/MapsLifecyclePanel";
import { SitemapAutoSubmit } from "./components/SitemapAutoSubmit";
import { lazyWithRetry as lazy } from "./lib/lazy-with-retry";

const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Reports = lazy(() => import("./pages/Reports"));
const ReportNew = lazy(() => import("./pages/ReportNew"));
const ReportDetail = lazy(() => import("./pages/ReportDetail"));
const ReportPreview = lazy(() => import("./pages/ReportPreview"));
const ReportFull = lazy(() => import("./pages/ReportFull"));
const Sites = lazy(() => import("./pages/Sites"));
const SiteDetail = lazy(() => import("./pages/SiteDetail"));
const FieldPortal = lazy(() => import("./pages/FieldPortal"));
const Company = lazy(() => import("./pages/Company"));
const Profile = lazy(() => import("./pages/Profile"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const AlertTrends = lazy(() => import("./pages/AlertTrends"));
const FieldReadings = lazy(() => import("./pages/FieldReadings"));
const NotificationHistory = lazy(() => import("./pages/NotificationHistory"));
const Inbox = lazy(() => import("./pages/Inbox"));
const InsightsPage = lazy(() => import("./pages/Insights"));
const DocumentsPage = lazy(() => import("./pages/Documents"));
const NotificationDeliveryLog = lazy(() => import("./pages/NotificationDeliveryLog"));
const ComplianceAudit = lazy(() => import("./pages/ComplianceAudit"));
const NotificationAudit = lazy(() => import("./pages/NotificationAudit"));
const InboxGrouped = lazy(() => import("./pages/InboxGrouped"));
const Reminders = lazy(() => import("./pages/Reminders"));
const EscalationPolicies = lazy(() => import("./pages/EscalationPolicies"));
const EscalationSimulator = lazy(() => import("./pages/EscalationSimulator"));
const ConflictSimulator = lazy(() => import("./pages/ConflictSimulator"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Diagnostics = lazy(() => import("./pages/Diagnostics"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const McpAuditLog = lazy(() => import("./pages/McpAuditLog"));
const Gis = lazy(() => import("./pages/Gis"));
const GscPerformance = lazy(() => import("./pages/GscPerformance"));
const SeoOps = lazy(() => import("./pages/SeoOps"));
const SeoPreview = lazy(() => import("./pages/SeoPreview"));
const SignupFunnel = lazy(() => import("./pages/SignupFunnel"));


const queryClient = new QueryClient();

const App = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setFadeOut(true), 1400);
    const hideTimer = setTimeout(() => setShowSplash(false), 1800);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  return (
    <>
      {showSplash && (
        <div style={{ animation: fadeOut ? "fadeOut 0.4s ease-out forwards" : undefined }}>
          <SplashScreen />
        </div>
      )}
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <SitemapAutoSubmit />
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/" element={<Landing />} />
                  <Route path="/sample-reports" element={<PublicReports />} />
                  <Route path="/demo" element={<PublicDemo />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/diagnostics" element={<Diagnostics />} />
                  <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

                  <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/reports/new/phase1-esa" element={<ReportNew />} />
                    <Route path="/reports/preview" element={<ReportPreview />} />
                    <Route path="/reports/:id" element={<ReportDetail />} />
                    <Route path="/reports/:id/full" element={<ReportFull />} />
                    <Route path="/sites" element={<Sites />} />
                    <Route path="/sites/:id" element={<SiteDetail />} />
                    <Route path="/field" element={<FieldPortal />} />
                    <Route path="/gis" element={<Gis />} />
                    <Route path="/company" element={<Company />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/inbox" element={<Inbox />} />
                    <Route path="/inbox/grouped" element={<InboxGrouped />} />
                    <Route path="/inbox/conflict-simulator" element={<ConflictSimulator />} />
                    <Route path="/reminders" element={<Reminders />} />
                    <Route path="/escalation-policies" element={<EscalationPolicies />} />
                    <Route path="/escalation-simulator" element={<EscalationSimulator />} />
                    <Route path="/notifications" element={<NotificationHistory />} />
                    <Route path="/alerts/trends" element={<AlertTrends />} />
                    <Route path="/field-readings" element={<FieldReadings />} />
                    <Route path="/insights" element={<InsightsPage />} />
                    <Route path="/documents" element={<DocumentsPage />} />
                    <Route path="/delivery-log" element={<NotificationDeliveryLog />} />
                    <Route path="/compliance-audit" element={<ComplianceAudit />} />
                    <Route path="/notification-audit" element={<NotificationAudit />} />
                    <Route path="/mcp-audit" element={<McpAuditLog />} />
                    <Route path="/seo/performance" element={<GscPerformance />} />
                    <Route path="/seo/ops" element={<SeoOps />} />
                    <Route path="/seo/preview" element={<SeoPreview />} />
                    <Route path="/admin/signups" element={<SignupFunnel />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
      <MapsLifecyclePanel />
    </>
  );
};

export default App;
